import { useState, useEffect, useCallback, useRef } from 'react'
import api from 'config/axiosConfig'
import { Entity, EntityType, Sort } from 'util/constants'
import IssueCard from '../Issues/IssueCard'
import Comment from 'components/Comments/Comment'
import CrosspostCard from 'components/Common/CrosspostCard'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import EntityCompactCard from './EntityCompactCard'
import CollectionCardRefactored, {
    type CollectionEntity,
} from 'components/Collections/CollectionCardRefactored'
import { useNavigate } from 'react-router-dom'
import { apiRoutes } from "util/apiRoutes";
import {
    PostCompactRenderer,
    PostFullRenderer,
} from "components/Posts/display/PostRendererRegistry";
import { resolvePostKind } from "components/Posts/display/resolvePostKind";

interface Props {
    entityTypes?: EntityType[]
    sort: Sort
    searchParams: URLSearchParams
    username?: string
    actor?: number
    issueId?: number
    isActorPage?: boolean
    isLoading: (isLoading: boolean) => void
    cardFormat?: 'full' | 'compact'
    showVote?: boolean
    emptyState?: React.ReactNode
    onEntityClick?: (entity: Entity) => void
}

interface EntitiesQueryParams {
    entityType?: string
    sortField: string
    sortOrder: string
    limit: number
    offset: number
    dimensionFilter?: string
    coauthorId?: number
    username?: string
    issueId?: number
    [key: string]: string | number | undefined
}

const EntitiesList = ({ entityTypes, sort, searchParams, username, actor, issueId, isActorPage = false, isLoading, cardFormat = 'full', showVote = true, emptyState, onEntityClick }: Props) => {
    const [entities, setEntities] = useState<Entity[]>([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const navigate = useNavigate()
    const entityTypesKey = entityTypes?.join(',') ?? ''
    const searchParamsKey = searchParams.toString()

    const fetchEntities = useCallback(async (page: number) => {
        if (!actor && !username && isActorPage) {
            setLoading(false)
            isLoading(false)
            return
        }

        const normalizedEntityTypes = entityTypesKey ? (entityTypesKey.split(',') as EntityType[]) : undefined
        const normalizedSearchParams = new URLSearchParams(searchParamsKey)
        const isPaginatedRequest = page > 1

        try {
            setLoading(true)
            // Only show parent-level skeleton for pagination, not initial load.
            isLoading(isPaginatedRequest)
            // After the unified-posts migration (`backend/sql/migration/2026031801_unify_posts.sql`)
            // and the `entity` view rewrite (`2026031804_entity_views.sql`), the
            // `entity_type` column on the unified view IS `posts.kind`. That means
            // `teabate` is a first-class entity type and must be sent to the API as-is —
            // any client-side fold to `impact` would silently drop teabate rows in
            // every feed and is a bug. The dimensionFilter is forwarded only when the
            // URL search params explicitly set it (e.g. the Teabates-with-dimensions
            // tab); we never auto-apply one based on the resolved entity-type set.
            let params: EntitiesQueryParams = {
                entityType: normalizedEntityTypes ? normalizedEntityTypes.join(',') : undefined,
                sortField: sort === 'top' || sort === 'bottom' ? 'vote_count' : 'created_at',
                sortOrder: sort === 'top' || sort === 'new' ? 'desc' : 'asc',
                limit: 10,
                offset: (page - 1) * 10,
                ...Object.fromEntries(normalizedSearchParams)
            }

            const existingDimensionFilter = normalizedSearchParams.get('dimensionFilter');
            if (existingDimensionFilter) {
                params.dimensionFilter = existingDimensionFilter;
            }
            
            if (actor) {
                params.coauthorId = actor
            }
            if (!actor && username) {
                params.username = username
            }
            if (issueId) {
                params.issueId = issueId
            }
            const response = await api.get(apiRoutes.entities.root, {
                params: params,
            })

            if (response.data.length < 10) {
                setHasMore(false)
            }

            setEntities((prevEntities) => {
                const newEntities = response.data.filter(
                    (newEntity: Entity) => !prevEntities.some((entity) => entity.eid === newEntity.eid)
                )
                return [...prevEntities, ...newEntities]
            })
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setError('404: Failed to fetch entities')
            } else {
                setError('Failed to fetch entities')
            }
        } finally {
            setLoading(false)
            isLoading(false)
        }
    }, [entityTypesKey, sort, searchParamsKey, actor, username, issueId, isActorPage, isLoading])

    useEffect(() => {
        setEntities([])
        setPage(1)
        setHasMore(true)
        fetchEntities(1)
    }, [fetchEntities])

    useEffect(() => {
        if (page > 1) {
            fetchEntities(page)
        }
    }, [page, fetchEntities])

    const observer = useRef<IntersectionObserver | null>(null);
    const lastEntityElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const wrapEntityCard = (entity: Entity, content: React.ReactNode) => {
        if (!onEntityClick) {
            return content;
        }

        return (
            <div
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('a, button')) {
                        return;
                    }
                    onEntityClick(entity);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onEntityClick(entity);
                    }
                }}
            >
                {content}
            </div>
        );
    }

    const renderEntityCard = (entity: Entity) => {
        const { entity_type, attributes, eid } = entity

        // If compact format is requested, use EntityCompactCard for all entity types
        if (cardFormat === 'compact') {
            // Skip comment type for compact cards as it has special handling
            if (entity_type === 'comment') {
                const isCrosspost = attributes.properties?.is_cross_post === true;
                if (isCrosspost) {
                    return (
                        <CrosspostCard
                            key={eid}
                            crosspostComment={attributes}
                        />
                    );
                }
                return (
                    <Comment
                        key={eid}
                        {...attributes}
                        depth={0}
                        maxDepth={0}
                        isAllCollapsed={true}
                        onAddReply={() => {}}
                        onUpdateComment={() => {}}
                        onDeleteComment={() => {}}
                        commentable_type={attributes.commentable_type}
                        commentable_id={attributes.commentable_id}
                    />
                );
            }

            if (entity_type === 'issue') {
                return wrapEntityCard(entity, (
                    <EntityCompactCard
                        key={eid}
                        entity={attributes}
                        displayEntity={attributes}
                        entityId={attributes.id}
                        entityType={entity_type}
                        showMeta={true}
                        showActions={true}
                        compactCard={true}
                        showVote={showVote}
                        onClick={() => {
                            navigate(`/${entity_type}/${attributes.id}`);
                        }}
                    />
                ));
            }

            // Collections don't fit the post-backed `PostCompactRenderer` (it
            // deliberately excludes collections), so render the compact entity
            // chip directly. The EntityAvatar layer already knows how to format
            // a "collection" avatar and link back to `/collection/:id`.
            if (entity_type === 'collection') {
                return wrapEntityCard(entity, (
                    <EntityCompactCard
                        key={eid}
                        entity={attributes}
                        displayEntity={attributes}
                        entityId={attributes.id}
                        entityType={entity_type}
                        showMeta={true}
                        showActions={true}
                        compactCard={true}
                        showVote={showVote}
                        onClick={() => {
                            navigate(`/collection/${attributes.id}`);
                        }}
                    />
                ));
            }

            return wrapEntityCard(entity, (
                <PostCompactRenderer
                    key={eid}
                    entity={attributes}
                    entityType={entity_type}
                    showMeta={true}
                    showActions={true}
                    showVote={showVote}
                />
            ));
        }

        // Otherwise use full cards
        switch (entity_type) {
            case 'issue':
                return wrapEntityCard(entity, <IssueCard key={eid} issue={attributes} showMeta={true} showActions={true} truncateDescription={true} />)
            case 'comment':
                // Check if this is a crosspost with content
                const isCrosspost = attributes.properties?.is_cross_post === true;
                
                // Render CrosspostCard if it's a crosspost with content
                if (isCrosspost) {
                    return (
                        <CrosspostCard
                            key={eid}
                            crosspostComment={attributes}
                        />
                    );
                }
                
                // Otherwise render regular Comment component
                return (
                    <Comment
                        key={eid}
                        {...attributes}
                        depth={0}
                        maxDepth={0}
                        isAllCollapsed={true}
                        onAddReply={() => {}}
                        onUpdateComment={() => {}}
                        onDeleteComment={() => {}}
                        commentable_type={attributes.commentable_type}
                        commentable_id={attributes.commentable_id}
                    />
                )
            case 'collection':
                // Collections are post-backed but intentionally excluded from
                // `PostDisplayKind`/`PostFullRenderer` because they have their
                // own `CollectionEntity` shape and graph/library variants. Use
                // the dedicated card so vote/save/share/edit and the graph-vs-
                // library branching all come through.
                return wrapEntityCard(entity, (
                    <CollectionCardRefactored
                        key={eid}
                        collection={attributes as CollectionEntity}
                        truncateDescription={true}
                        isEntityProfilePage={false}
                    />
                ))
            default:
                if (!resolvePostKind(entity_type, attributes)) {
                    return (
                        <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any posts of this type.`} />
                    )
                }

                return wrapEntityCard(entity, <PostFullRenderer key={eid} entity={attributes} entityType={entity_type} context="feed" />)
        }
    }

    if (loading && page === 1) {
        return (
            <LoadingSkeleton variant={cardFormat === 'compact' ? 'entity-avatar' : 'entity-card'} count={5} />
        )
    }

    if (error) {
        return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any posts of this type.`} /> : <ServerErrorPage />
    }

    if (!loading && entities.length === 0 && emptyState) {
        return (
            <div className="entity-list-empty-state">
                {emptyState}
            </div>
        )
    }

    return (
        <div className="bg-transparent overflow-x-hidden">
            <div className="entity-divider flex flex-col">
                {entities.map((entity, index) => (
                    <div
                        key={entity.eid}
                        ref={index === entities.length - 1 ? lastEntityElementRef : null}
                    >
                        {renderEntityCard(entity)}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default EntitiesList